import React from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Container,
  MenuItem,
  Button,
  Typography,
  Grid,
  Backdrop,
  CircularProgress,
  useTheme,
} from "@material-ui/core";
import { CheckboxWithLabel, TextField } from "formik-material-ui";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import {
  useProjectDetails,
  useProjectManagers,
} from "../../projectManagementQueries";
import { dispositionProjectUpdate, useProjectFactoryWitness, useUpdateProject, useUpdateProjectforCustomer } from "../../projectManagementMutations";
import { useDispositions, useProjectDispositions } from "../../../common/services/dispositionServices";
import { useCustomerDetailsContext } from "../../../common/CustomerDetailsContext";
import { useAuthContext } from "../../../common/AuthContext";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { ViewNote } from "../../../common/ViewNoteDialog";
import { processErrorOnMutation } from "../../../../util/errorMessaging";

import { useProjectNotes } from "../../projectQueries";
import { ViewAddNoteList } from "../../../common/ViewAddNotes";
import { stringSanitize } from "../../../common/util";
import { Messaging } from "../../../common/enums";
import { useLocations } from "../../../intake/intakeQueries";
import { useFetchContext } from "../../../common/FetchContext";
import LSDBFormikAutocompleteField from "../../../common/LSDBFormikAutocompleteField";
import { usesubmitMaxTolerance } from "../../../common/CommonMutations";

export const EditProject = () => {
  const { state } = useCustomerDetailsContext();
  const { dispatch } = useAuthContext();
  const { authAxios } = useFetchContext();
  const history = useHistory();
  const theme = useTheme();
  const { projectId } = useParams() as {
    projectId: string;
    customerId: string;
  };
  const { mutateAsync: mutate } = useUpdateProjectforCustomer();
  const { mutateAsync: mutateFactoryWitness } = useProjectFactoryWitness();
  const { mutateAsync: mutateAddMaxTolerance } = usesubmitMaxTolerance();


  const {
    error: errorProjectDetails,
    data: projectDetails,
    isLoading: isLoadingProjectDetails,
    isError: isErrorProjectDetails,
    isSuccess: isSuccessProjectDetails,
  } = useProjectDetails(projectId);

  const {
    error: errorLocation,
    data: locationData,
    isLoading: isLoadingLocation,
    isError: isErrorLocation,
  } = useLocations();

  const {
    error: errorDispositions,
    data: dispositions,
    isLoading: isLoadingDispositions,
    isError: isErrorDispositions,
  } = useProjectDispositions();

  const {
    error: errorProjectManagers,
    data: projectManagers,
    isError: isErrorProjectManagers,
  } = useProjectManagers();

  const [viewNote, setViewNote] = React.useState(false);

  const [selectedNote, setSelectedNote] = React.useState({});

  const handleClose = () => {
    setViewNote(!viewNote);
  };

  if (isErrorProjectDetails || isErrorDispositions || isErrorProjectManagers) {
    return (
      <>
        {isErrorProjectDetails && <ErrorMessage error={errorProjectDetails} />}
        {isErrorDispositions && <ErrorMessage error={errorDispositions} />}
        {isErrorProjectManagers && (
          <ErrorMessage error={errorProjectManagers} />
        )}
      </>
    );
  }

  if (
    isLoadingProjectDetails ||
    isLoadingDispositions ||
    isLoadingDispositions
  ) {
    return (
      <Backdrop
        open={
          isLoadingProjectDetails ||
          isLoadingDispositions ||
          isLoadingDispositions
        }
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <div style={theme.container}>
      <Container>
        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="center"
          spacing={4}
        >
          {isSuccessProjectDetails && (
            <Grid item xs={12}>
              <Typography variant="h5">
                Edit Project for Customer {projectDetails.customer_name}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Formik
              initialValues={{
                number: projectDetails?.number,
                sfdc_number: projectDetails?.sfdc_number,
                project_manager: projectDetails?.project_manager,
                disposition: projectDetails?.disposition,
                proposal_price: projectDetails?.proposal_price,
                project_loaction: projectDetails?.location,
                Factory_Witness: projectDetails?.factory_witness,
                isc: projectDetails?.maxtolerance?.isc ?? "",
                voc: projectDetails?.maxtolerance?.voc ?? "",
                pmp: projectDetails?.maxtolerance?.pmp ?? "",
              }}
              validationSchema={Yup.object({
                number: Yup.string()
                  .required("Field must be required")
                  .test(
                    "sanitize",
                    Messaging.UserInput,
                    (value: string | undefined) =>
                      value ? stringSanitize(value) : false
                  ),
                sfdc_number: Yup.string()
                  .required("Field must be required")
                  .test(
                    "sanitize",
                    Messaging.UserInput,
                    (value: string | undefined) =>
                      value ? stringSanitize(value) : false
                  ),
                project_manager: Yup.string().required(
                  "Field must be required"
                ),
                disposition: Yup.string().required("Field must be required"),
                proposal_price: Yup.string().required("Field must be required"),
              })}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  console.log("Form values", JSON.stringify(values));

                  const newProjectData = await mutate({
                    projectId,
                    number: values.number,
                    sfdc_number: values.sfdc_number,
                    project_manager: values.project_manager,
                    disposition: values.disposition,
                    group: projectDetails.group,
                    customer: projectDetails.customer,
                    proposal_price: values.proposal_price,
                    start_date: "",
                    comments: "",
                    location: values.project_loaction,
                  });
                  await mutateAddMaxTolerance({
                    authAxios,
                    project_id: Number(projectId),
                    isc: Number(values.isc) || 0,
                    voc: Number(values.voc) || 0,
                    pmp: Number(values.pmp) || 0,
                  });
                  const newProjectFactoryWitnessData = await mutateFactoryWitness({
                    authAxios,
                    project: projectId,
                    factory_witness: values.Factory_Witness
                  });
                  console.log("Project data", newProjectData);
                  setSubmitting(false);

                  history.push(
                    `/project_management/customer/${state.activeCustomerId}`
                  );
                  toast.success("Project was succesfully changed");
                } catch (error) {
                  toast.error("Error while modifying project.");
                  processErrorOnMutation(error, dispatch, history);
                }
              }}
            >
              {({ errors, touched, submitForm, resetForm }) => (
                <Form>
                  <Grid
                    container
                    direction="row"
                    justify="space-between"
                    alignItems="center"
                    spacing={4}
                  >
                    <Grid item sm={12}>
                      <Field
                        id="number"
                        type="text"
                        name="number"
                        fullWidth
                        component={TextField}
                        helperText={touched.number ? errors.number : ""}
                        error={touched.number && Boolean(errors.number)}
                        style={{ marginBottom: 32 }}
                        label="Project Number"
                        data-testid="project_number"
                      ></Field>

                      <Field
                        name="sfdc_number"
                        helperText={
                          touched.sfdc_number ? errors.sfdc_number : ""
                        }
                        error={
                          touched.sfdc_number && Boolean(errors.sfdc_number)
                        }
                        component={TextField}
                        data-testid="sfdc_number"
                        label="SFDC Number"
                        style={{ marginBottom: 32 }}
                        placeholder="Write an SFDC number"
                        fullWidth
                        margin="dense"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      {/* <Field
                        id="project_loaction"
                        type="text"
                        name="project_loaction"
                        select={true}
                        fullWidth
                        component={TextField}
                        helperText={
                          touched.project_loaction ? errors.project_loaction : ""
                        }
                        error={
                          touched.project_loaction &&
                          Boolean(errors.project_loaction)
                        }
                        style={{ marginBottom: 32 }}
                        label="Project Location"
                        data-testid="project_loaction"
                      >
                        {locationData?.map((location: any) => (
                          <MenuItem
                            key={location.id}
                            value={location.url}
                          >
                            {location.name}
                          </MenuItem>
                        ))}
                      </Field> */}
                      <LSDBFormikAutocompleteField
                        fieldId="project_loaction"
                        fieldName="project_loaction"
                        fieldDisplayName="Project Location"
                        options={locationData}
                        getOptionLabel={(location: any) => location.name}
                        getOptionValue={(project: any) => project.url}
                        fullWidth={true}
                        variant="standard"
                      />

                      {/* <Field
                        id="project_manager"
                        type="text"
                        name="project_manager"
                        select={true}
                        fullWidth
                        component={TextField}
                        helperText={
                          touched.project_manager ? errors.project_manager : ""
                        }
                        error={
                          touched.project_manager &&
                          Boolean(errors.project_manager)
                        }
                        style={{ marginBottom: 32 }}
                        label="Project Manager"
                        data-testid="project_manager"
                      >
                        {projectManagers?.map((projectManager: any) => (
                          <MenuItem
                            key={projectManager.id}
                            value={projectManager.url}
                          >
                            {projectManager.first_name}{" "}
                            {projectManager.last_name}
                          </MenuItem>
                        ))}
                      </Field> */}
                      <LSDBFormikAutocompleteField
                        fieldId="project_manager"
                        fieldName="project_manager"
                        fieldDisplayName="Project Manager"
                        options={projectManagers}
                        getOptionLabel={(pm: any) =>
                          `${pm.first_name || ""} ${pm.last_name || ""}`
                        }
                        getOptionValue={(pm: any) => pm.url}
                        fullWidth={true}
                        variant="standard"
                      />
                      {/* <Field
                        id="disposition"
                        type="text"
                        name="disposition"
                        select={true}
                        fullWidth
                        component={TextField}
                        helperText={
                          touched.disposition ? errors.disposition : ""
                        }
                        error={
                          touched.disposition && Boolean(errors.disposition)
                        }
                        style={{ marginBottom: 32 }}
                        label="Disposition"
                        data-testid="disposition"
                      >
                        {dispositions?.map((disposition: any) => (
                          <MenuItem
                            key={disposition.id}
                            value={disposition.url}
                          >
                            {disposition.name}
                          </MenuItem>
                        ))}
                      </Field> */}
                      <LSDBFormikAutocompleteField
                        fieldId="disposition"
                        fieldName="disposition"
                        fieldDisplayName="Disposition"
                        options={dispositions}
                        getOptionLabel={(disposition: any) => disposition.name}
                        getOptionValue={(disposition: any) => disposition.url}
                        fullWidth={true}
                        variant="standard"
                      />
                      <Field
                        id="number"
                        type="number"
                        name="proposal_price"
                        fullWidth
                        component={TextField}
                        helperText={
                          touched.proposal_price ? errors.proposal_price : ""
                        }
                        error={
                          touched.proposal_price &&
                          Boolean(errors.proposal_price)
                        }
                        style={{ marginBottom: 32 }}
                        label="Contract Value"
                        data-testid="proposal_price"
                      ></Field>
                      <Field
                        component={TextField}
                        label="ISC"
                        name="isc"
                        type="number"
                        variant="standard"
                        fullWidth
                        style={{ marginBottom: 12 }}
                      />

                      <Field
                        component={TextField}
                        label="VOC"
                        name="voc"
                        type="number"
                        variant="standard"
                        fullWidth
                        style={{ marginBottom: 12 }}
                      />

                      <Field
                        component={TextField}
                        label="PMP"
                        name="pmp"
                        type="number"
                        variant="standard"
                        fullWidth
                        style={{ marginBottom: 32 }}
                      />
                      <Field
                        id="Factory_Witness"
                        data-testid="Factory_Witness"
                        name="Factory_Witness"
                        type="checkbox"
                        component={CheckboxWithLabel}
                        helperText={touched.Factory_Witness ? errors.Factory_Witness : ""}
                        error={touched.Factory_Witness && Boolean(errors.Factory_Witness)}
                        fullWidth
                        margin="dense"
                        Label={{ label: "Factory Witness" }}
                        disabled={projectDetails?.factory_witness === true}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      {projectDetails.notes && (
                        <ViewAddNoteList
                          id={projectDetails.id}
                          type={1}
                          model={"project"}
                          invalidate={["project", projectDetails.id]}
                          count={projectDetails.notes[0]?.count}
                          getNotes={useProjectNotes}
                        />
                      )}
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={3}
                    direction="row"
                    justify="space-around"
                    alignItems="center"
                    style={{ marginTop: 32 }}
                  >
                    <Grid item xs={2}>
                      <Button
                        data-testid="submitCrateId"
                        variant="contained"
                        color="primary"
                        onClick={submitForm}
                      >
                        Submit
                      </Button>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => resetForm()}
                      >
                        Undo Changes
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
        <ViewNote
          note={selectedNote}
          open={viewNote}
          handleClose={handleClose}
        />
      </Container>
    </div>
  );
};
